import React, { Fragment } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';

import { UploadPermissionBlock } from '../../../services/contributors';
import { getAsset } from '../../../../../util/assets';
import {
  hasUnconfiguredTeamJerseys,
  isAlbum,
  isSpecialtyAlbum,
  isVideoAlbum,
  isWeddingsContent,
  requiresGreenScreenCheck,
} from '../../../../../util/album';
import {
  folderHasAlbums,
  getUploadUrl,
  shouldShowPricingCategoryHierarchyGreenScreenModal,
} from '../../../../../util/album-folder';
import ListingCardTemplateLink from './ListingCardTemplateLink';
import Button from '../../../../../components/global/Button';
import useOpenModal from '../../../../../hooks/useOpenModal';
import useRequestConfirm from '../../../../../hooks/useRequestConfirm';
import useDismissiblePersistent from '../../../../../hooks/useDismissiblePersistent';
import { CONTRIBUTOR_SHARE_MODAL } from '../../../../../constants/modals';
import { PHOTO_UPLOAD_TYPE } from '../../../../../constants/photo-types';
import { calculateStylesForCoverPhoto } from '../helpers';
import { PRICING_CATEGORY_HIERARCHY_GREEN_SCREEN_SETTINGS_MODAL } from '../../../../../components/global/PricingCategoryHierarchyGreenScreenSettingsModal';

const propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string,
  }),
  account: PropTypes.shape({
    id: PropTypes.string,
  }),
  title: PropTypes.string,
  linkTo: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  basename: PropTypes.string,
  nullState: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  thumbnailUrl: PropTypes.string,
  thumbnailNullText: PropTypes.string,
  thumbnailChildren: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  isDraft: PropTypes.bool,
  templateLinkProps: PropTypes.shape({
    linkTo: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    onClick: PropTypes.func,
  }),
};

const ListingCardTemplateThumbnail = ({
  entity,
  account,
  title,
  linkTo,
  basename,
  nullState,
  thumbnailUrl,
  thumbnailChildren,
  thumbnailNullText,
  isDraft,
  templateLinkProps,
}) => {
  const history = useHistory();
  const location = useLocation();
  const openModal = useOpenModal();
  const requestConfirm = useRequestConfirm();
  const {
    visible: groupPhotosReminderVisible,
    onDismiss: onGroupPhotosReminderDismiss,
  } = useDismissiblePersistent(
    isAlbum(entity) ? `${entity?.id}_GROUP_PHOTOS_REMINDER_SEEN` : null,
  );
  const isWeddingsGallery = !isAlbum(entity) && isWeddingsContent(entity);
  const isSpecialtyAlbumBool = isAlbum(entity) && isSpecialtyAlbum(entity);
  const isGroupVideoBool = isAlbum(entity) && isVideoAlbum(entity);
  const albumAlbumFolderTags = isAlbum(entity)
    ? entity?.album_folder_tags?.records ?? []
    : [];

  const hasUnconfiguredTeamJerseysBool = hasUnconfiguredTeamJerseys(
    albumAlbumFolderTags,
  );

  const { cover_photos: { records } = {}, cover_photo } = entity || {
    cover_photos: { faces_box: [] },
    cover_photo: [],
  };

  const faceCoordinates = records?.[0]?.faces_box
    ? records[0]?.faces_box
    : cover_photo?.faces_box;

  const imageStyles =
    faceCoordinates && calculateStylesForCoverPhoto(faceCoordinates);

  if (!thumbnailUrl) {
    const cardClasses = classNames({
      'listing-card-template-cover-null-state listing-card-template-cover-null-state-actions text-uppercase': true,
    });

    if (nullState) {
      return (
        <ListingCardTemplateLink {...templateLinkProps}>
          {nullState}
        </ListingCardTemplateLink>
      );
    }

    const UploadContentComponent = isContributor => {
      if (isDraft) {
        return null;
      }

      if (hasUnconfiguredTeamJerseysBool) {
        return <>Click to view!</>;
      }

      return (
        <Fragment>
          <div>
            {isSpecialtyAlbumBool
              ? `${isContributor ? 'Help build the' : 'Build your'}`
              : 'Got Photos?'}
          </div>
          <div>
            {isSpecialtyAlbumBool
              ? `${isGroupVideoBool ? 'Group Video!' : 'Memories Slideshow!'}`
              : 'Add Them Now!'}
          </div>
        </Fragment>
      );
    };

    return (
      <UploadPermissionBlock
        entityId={entity?.id}
        checkOwnership
        isOwner={(entity?.account?.id ?? entity?.creator?.id) === account?.id}
        fallbackContent={() => (
          <ListingCardTemplateLink {...templateLinkProps}>
            <div className="listing-card-template-cover-null-state">
              <img
                src={getAsset('/waldo-icon.svg')}
                alt=""
                className="album-folders-null-state-icon"
              />
              <p>{thumbnailNullText}</p>
            </div>
          </ListingCardTemplateLink>
        )}
      >
        {({ isContributor }) => (
          <ListingCardTemplateLink {...templateLinkProps}>
            <div className={cardClasses}>
              <UploadContentComponent isContributor={isContributor} />
              {!isDraft && (
                <div className="listing-card-template-cover-null-state-actions-buttons">
                  {entity &&
                    (isAlbum(entity) || folderHasAlbums(entity)) &&
                    !hasUnconfiguredTeamJerseysBool && (
                      <Button
                        btnStyle={!isSpecialtyAlbumBool ? 'white' : 'primary'}
                        btnOutline
                        btnSize="xs"
                        onClick={e => {
                          e.stopPropagation();
                          e.preventDefault();

                          if (isSpecialtyAlbumBool && !isContributor) {
                            history.push(linkTo);
                            return;
                          }

                          if (isContributor && isGroupVideoBool) {
                            history.push(
                              `/contributors/upload/${entity.id}/record-video`,
                            );
                            return;
                          }

                          if (isContributor) {
                            history.push(`/contributors/upload/${entity.id}`);
                            return;
                          }

                          if (isAlbum(entity)) {
                            if (
                              requiresGreenScreenCheck(entity) &&
                              entity.root_album_folder.picture_day_enabled
                            ) {
                              return;
                            }

                            history.push({
                              pathname: `/app/${basename}/albums/${entity.id}/upload`,
                              state: {
                                albumId: entity.id,
                              },
                            });
                            return;
                          }

                          if (
                            shouldShowPricingCategoryHierarchyGreenScreenModal(
                              entity,
                              true,
                            )
                          ) {
                            openModal(
                              PRICING_CATEGORY_HIERARCHY_GREEN_SCREEN_SETTINGS_MODAL,
                              {
                                albumFolderId: entity.id,
                                onSuccess: () =>
                                  history.push({
                                    pathname: getUploadUrl(
                                      basename,
                                      entity?.root ?? entity,
                                    ),
                                    state: {
                                      ...location.state,
                                      albumFolderId: entity?.id,
                                    },
                                  }),
                              },
                            );
                            return;
                          }

                          const isGroupPhotosOnly =
                            entity?.album_folder?.team_photos_upload_type &&
                            entity?.album_folder?.team_photos_upload_type !==
                              PHOTO_UPLOAD_TYPE.NOT_UPLOADING &&
                            entity?.album_folder
                              ?.individual_photos_upload_type ===
                              PHOTO_UPLOAD_TYPE.NOT_UPLOADING;

                          if (isGroupPhotosOnly && groupPhotosReminderVisible) {
                            requestConfirm({
                              message: (
                                <div className="text-center">
                                  <h4>Reminder</h4>
                                  <p>
                                    It looks like you are about to upload group
                                    photos. Please confirm that all group photos
                                    have the correct spacing to allow for an
                                    8x10 crop.
                                  </p>
                                </div>
                              ),
                              confirmText: 'Confirm',
                              onConfirm: () => {
                                onGroupPhotosReminderDismiss();
                                history.push({
                                  pathname: `/app/${basename}/albums/${entity.id}/upload`,
                                  state: {
                                    albumId: entity.id,
                                  },
                                });
                              },
                              actionSize: 'sm',
                            });
                            return;
                          }

                          history.push({
                            pathname: getUploadUrl(
                              basename,
                              entity?.root ?? entity,
                            ),
                            state: {
                              ...location.state,
                              albumFolderId: entity?.id,
                            },
                          });
                        }}
                      >
                        {isSpecialtyAlbumBool && !isContributor
                          ? 'Build'
                          : 'Upload'}
                      </Button>
                    )}

                  {!isContributor && (
                    <Button
                      btnStyle={!isSpecialtyAlbumBool ? 'white' : 'primary'}
                      btnOutline
                      btnSize="xs"
                      onClick={event => {
                        event.preventDefault();
                        openModal(CONTRIBUTOR_SHARE_MODAL, {
                          albumId: isAlbum(entity) ? entity.id : undefined,
                          albumFolderId: !isAlbum(entity)
                            ? entity.id
                            : undefined,
                        });
                      }}
                    >
                      {isSpecialtyAlbumBool
                        ? `Request ${isGroupVideoBool ? 'videos' : 'photos'}`
                        : `${
                            entity?.picture_day_enabled ? 'Market' : 'Invite'
                          }`}
                    </Button>
                  )}
                  {!isContributor && isWeddingsGallery && (
                    <Button
                      btnBlock
                      btnStyle={!isSpecialtyAlbumBool ? 'white' : 'primary'}
                      btnOutline
                      btnSize="xs"
                      onClick={event => {
                        event.preventDefault();
                        history.push({
                          pathname: `/app/${basename}/albums/create`,
                          state: {
                            albumFolder: entity,
                          },
                        });
                      }}
                    >
                      Add Album
                    </Button>
                  )}
                </div>
              )}
            </div>
          </ListingCardTemplateLink>
        )}
      </UploadPermissionBlock>
    );
  }

  return (
    <ListingCardTemplateLink {...templateLinkProps}>
      <img
        style={imageStyles}
        src={thumbnailUrl}
        alt={title}
        className="listing-card-template-cover-img"
      />
      {thumbnailChildren}
    </ListingCardTemplateLink>
  );
};

ListingCardTemplateThumbnail.propTypes = propTypes;

export default ListingCardTemplateThumbnail;
